import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Tab,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore, MarkEmailReadOutlined, Refresh, SendOutlined, VisibilityOutlined } from '@mui/icons-material';
import { ListSkeleton, SectionCard } from '../pagekit';
import { campaignHistory, listCampaigns, previewCampaign, sendCampaign, suppressAddress, testSendCampaign } from './marketingApi';
import { formatDate, ScrollTable, StatTile, ToneChip } from './consoleUi';

/** Order hint shown above the cards. */
export const ORDER_HINT = "Send What's new first. Re-engagement emails skip anyone emailed in the last 14 days.";

/** Readable label per skip reason from the audience function. */
export const SKIP_REASON_LABELS = {
  opted_out: 'Unsubscribed',
  suppressed: 'Bounced, complained or suppressed',
  emailed_recently: 'Emailed in the last 14 days',
  already_received: 'Already received this campaign',
  outside_segment: 'Outside this segment',
  unconfirmed_or_never_signed_in: 'Unconfirmed or never signed in',
  banned: 'Banned account',
  platform_admin: 'Platform admin (use a test send)',
  duplicate_email: 'Duplicate email address',
};

/** Tone and label per marketing_sends status. */
const SEND_STATUS_META = {
  queued: { label: 'Queued', tone: 'neutral' },
  sent: { label: 'Sent', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
  bounced: { label: 'Bounced', tone: 'warning' },
  complained: { label: 'Complained', tone: 'danger' },
};

/** Secret name per config flag, in the order they should be set. */
const REQUIRED_SECRETS = [
  ['apiKeyConfigured', 'RESEND_API'],
  ['fromConfigured', 'MARKETING_FROM'],
  ['postalConfigured', 'MARKETING_POSTAL_ADDRESS'],
];

/**
 * Secrets that still need to be set, from the list config.
 * @param {object|undefined} config - list config.
 * @returns {string[]} Missing secret names.
 */
export function missingSecrets(config) {
  if (!config) return [];
  return REQUIRED_SECRETS.filter(([flag]) => config[flag] === false).map(([, name]) => name);
}

/**
 * "1 person" / "3 people".
 * @param {number} n - Count.
 * @returns {string} Phrase.
 */
export const people = (n) => `${n} ${n === 1 ? 'person' : 'people'}`;

/**
 * Number with grouping, or 0.
 * @param {unknown} n - Value.
 * @returns {string} Formatted.
 */
const num = (n) => (Number.isFinite(Number(n)) ? Number(n) : 0).toLocaleString('en-US');

/**
 * Date and time ("Nov 2, 2026, 3:04 PM"), or a fallback.
 * @param {string|null|undefined} iso - ISO timestamp.
 * @param {string} [fallback='Never'] - Text when missing.
 * @returns {string} Text.
 */
export function formatDateTime(iso, fallback = 'Never') {
  if (!iso) return fallback;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return fallback;
  return `${formatDate(iso)}, ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

/**
 * Normalize the history reply (camelCase or snake_case rows).
 * @param {object|null|undefined} reply - history reply.
 * @returns {{runs: object[], sends: object[]}} Rows.
 */
export function normalizeHistory(reply) {
  const runs = (Array.isArray(reply?.runs) ? reply.runs : []).map((r, i) => ({
    id: r.id || `run-${i}`,
    campaignKey: r.campaignKey ?? r.campaign_key ?? '',
    startedAt: r.startedAt ?? r.started_at ?? null,
    finishedAt: r.finishedAt ?? r.finished_at ?? null,
    requested: r.requested ?? null,
    sent: r.sent ?? 0,
    failed: r.failed ?? 0,
    test: Boolean(r.test),
  }));
  const sends = (Array.isArray(reply?.sends) ? reply.sends : []).map((s, i) => ({
    id: s.id || `send-${i}`,
    campaignKey: s.campaignKey ?? s.campaign_key ?? '',
    email: s.email || '',
    status: s.status || 'queued',
    sentAt: s.sentAt ?? s.sent_at ?? null,
    createdAt: s.createdAt ?? s.created_at ?? null,
    error: s.error || null,
    test: Boolean(s.test),
  }));
  return { runs, sends };
}

/**
 * Human message for a failed call. A 404 here (after the console itself authorized) means the
 * marketing-campaigns function is not deployed yet.
 * @param {any} error - Error.
 * @param {string} fallback - Fallback text.
 * @returns {string} Message.
 */
function errorMessage(error, fallback) {
  if (error?.status === 404) return 'Campaigns are not available yet. Deploy the marketing-campaigns function, then try again.';
  return error?.message || fallback;
}

/** Title of the setup banner (also its accessible name). */
export const SETUP_TITLE = 'Sending is off until these Supabase secrets are set';
/** Title of the banner shown when a public email function rejects requests without a JWT. */
export const GATED_TITLE = 'These email functions reject public requests';

/**
 * Public functions the gateway rejects without a JWT, from the list config.
 * @param {object|undefined} config - list config.
 * @returns {string[]} Function names.
 */
export function gatedEndpoints(config) {
  return Array.isArray(config?.publicEndpointsGated) ? config.publicEndpointsGated.filter((n) => typeof n === 'string') : [];
}

/**
 * Monospace list of names.
 * @param {{names: string[]}} props - Props.
 * @returns {JSX.Element} List.
 */
function CodeList({ names }) {
  return (
    <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
      {names.map((name) => (
        <li key={name}>
          <Box component="code" sx={{ fontFamily: 'var(--q-font-mono, ui-monospace, monospace)', fontSize: 13 }}>{name}</Box>
        </li>
      ))}
    </Box>
  );
}

/**
 * Setup banner naming the exact secrets still missing, and any public email function the
 * gateway rejects (verify_jwt on), which would break unsubscribe links and bounce handling.
 * The alert is named by its visible title so its full text is still read.
 * @param {{missing: string[], gated: string[]}} props - Props.
 * @returns {JSX.Element|null} Banner.
 */
function SetupBanner({ missing, gated }) {
  if (!missing.length && !gated.length) return null;
  const testBlocked = missing.includes('RESEND_API') || missing.includes('MARKETING_FROM');
  const titleId = missing.length ? 'campaign-setup-title' : 'campaign-gated-title';
  return (
    <Alert severity="warning" role="alert" aria-labelledby={titleId}>
      {missing.length > 0 && (
        <>
          <Typography id="campaign-setup-title" component="div" sx={{ fontWeight: 600, fontSize: 14 }}>
            {SETUP_TITLE}
          </Typography>
          <CodeList names={missing} />
          <Typography component="div" variant="body2" sx={{ mt: 0.5 }}>
            {testBlocked
              ? 'Test sends need RESEND_API and MARKETING_FROM.'
              : 'Test sends work now; the footer shows a placeholder until the postal address is set.'}
          </Typography>
        </>
      )}
      {gated.length > 0 && (
        <Box sx={{ mt: missing.length ? 1.5 : 0 }}>
          <Typography id="campaign-gated-title" component="div" sx={{ fontWeight: 600, fontSize: 14 }}>
            {GATED_TITLE}
          </Typography>
          <CodeList names={gated} />
          <Typography component="div" variant="body2" sx={{ mt: 0.5 }}>
            Redeploy them with verify_jwt = false (pinned in supabase/config.toml) so unsubscribe links and bounce handling work.
          </Typography>
        </Box>
      )}
    </Alert>
  );
}

/**
 * One campaign card.
 * @param {object} props - Props.
 * @param {object} props.campaign - Campaign row from list.
 * @param {boolean} props.canSend - Setup complete and today's limit not used up.
 * @param {number} props.remainingToday - Sends left under today's limit.
 * @param {boolean} props.canTest - Test sends possible.
 * @param {string|null} props.blockedReason - Why sending is off (shown under the buttons).
 * @param {{status: string, message: string}|undefined} props.testState - Test send status.
 * @param {(c: object) => void} props.onPreview - Open the preview.
 * @param {(c: object) => void} props.onTest - Send a test.
 * @param {(c: object) => void} props.onSend - Open the confirm dialog.
 * @returns {JSX.Element} Card.
 */
function CampaignCard({ campaign, canSend, remainingToday, canTest, blockedReason, testState, onPreview, onTest, onSend }) {
  const [showSkips, setShowSkips] = useState(false);
  const eligible = Number(campaign.eligibleCount) || 0;
  const skipped = Object.entries(campaign.skipped || {}).filter(([, n]) => Number(n) > 0).sort((a, b) => b[1] - a[1]);
  const skippedTotal = skipped.reduce((sum, [, n]) => sum + Number(n), 0);
  const totals = campaign.totals || {};
  const stuck = Number(totals.stuck) || 0;
  const lastRun = campaign.lastRun || null;
  const skipsId = `campaign-skips-${campaign.key}`;
  const blockedId = `campaign-blocked-${campaign.key}`;
  const testing = testState?.status === 'sending';
  // Kept focusable (aria-disabled, not disabled) so keyboard users reach it and hear why it is off.
  const sendBlocked = !canSend || eligible === 0;
  const showReason = Boolean(blockedReason) && eligible > 0;
  const take = Math.max(0, Math.min(eligible, Number(remainingToday) || 0));
  const sendLabel = canSend && take < eligible ? `Send to ${num(take)} of ${num(eligible)}` : `Send to ${people(eligible)}`;

  return (
    <SectionCard title={campaign.name} description={campaign.description} aria-label={campaign.name} data-testid={`campaign-${campaign.key}`}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 1 }}>
          <Typography component="div" sx={{ fontSize: 26, fontWeight: 650, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
            {num(eligible)}
          </Typography>
          <Typography component="div" sx={{ fontSize: 13, color: 'var(--q-muted, #5E5A54)' }}>
            {eligible === 1 ? 'person eligible now' : 'people eligible now'}
          </Typography>
        </Box>

        <Box role="group" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }} aria-label={`Totals for ${campaign.name}`}>
          <ToneChip tone="success">{`${num(totals.sent)} sent`}</ToneChip>
          <ToneChip tone={totals.bounced ? 'warning' : 'neutral'}>{`${num(totals.bounced)} bounced`}</ToneChip>
          <ToneChip tone={totals.complained ? 'danger' : 'neutral'}>{`${num(totals.complained)} complained`}</ToneChip>
          {stuck > 0 && <ToneChip tone="warning">{`${num(stuck)} unconfirmed`}</ToneChip>}
        </Box>
        {stuck > 0 && (
          <Typography variant="body2" sx={{ color: 'var(--q-muted, #5E5A54)' }}>
            {`${num(stuck)} ${stuck === 1 ? 'email has' : 'emails have'} no confirmed result from Resend. ${stuck === 1 ? 'It counts' : 'They count'} as received, so nobody is emailed twice.`}
          </Typography>
        )}

        <Typography variant="body2" sx={{ color: 'var(--q-muted, #5E5A54)' }}>
          {lastRun
            ? `Last sent ${formatDateTime(lastRun.startedAt)}: ${num(lastRun.sent)} sent, ${num(lastRun.failed)} failed`
            : 'Not sent yet'}
        </Typography>

        {skippedTotal > 0 && (
          <Box>
            <Button
              size="small"
              onClick={() => setShowSkips((v) => !v)}
              aria-expanded={showSkips}
              aria-controls={skipsId}
              endIcon={<ExpandMore sx={{ transform: showSkips ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />}
              sx={{ textTransform: 'none', px: 0.5, color: 'var(--q-text-2, #3D3A35)' }}
            >
              {`${num(skippedTotal)} skipped`}
            </Button>
            <Collapse in={showSkips} unmountOnExit>
              <Box
                component="ul"
                id={skipsId}
                aria-label={`Skip reasons for ${campaign.name}`}
                sx={{ listStyle: 'none', m: 0, mt: 0.5, p: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}
              >
                {skipped.map(([reason, n]) => (
                  <Box component="li" key={reason} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: 13 }}>
                    <span>{SKIP_REASON_LABELS[reason] || reason.replace(/_/g, ' ')}</span>
                    <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', color: 'var(--q-muted, #5E5A54)' }}>{num(n)}</Box>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityOutlined fontSize="small" />}
            onClick={() => onPreview(campaign)}
            sx={{ textTransform: 'none', borderRadius: 'var(--q-radius-pill, 999px)' }}
          >
            Preview
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<MarkEmailReadOutlined fontSize="small" />}
            onClick={() => onTest(campaign)}
            disabled={!canTest || testing}
            sx={{ textTransform: 'none', borderRadius: 'var(--q-radius-pill, 999px)' }}
          >
            {testing ? 'Sending test' : 'Send test to me'}
          </Button>
          <Button
            size="small"
            variant="contained"
            disableElevation
            disableRipple={sendBlocked}
            startIcon={<SendOutlined fontSize="small" />}
            onClick={() => {
              if (!sendBlocked) onSend(campaign);
            }}
            aria-disabled={sendBlocked ? 'true' : undefined}
            aria-describedby={showReason ? blockedId : undefined}
            className={sendBlocked ? 'Mui-disabled' : undefined}
            sx={{ textTransform: 'none', borderRadius: 'var(--q-radius-pill, 999px)' }}
          >
            {sendLabel}
          </Button>
        </Box>
        {showReason && (
          <Typography id={blockedId} variant="body2" sx={{ color: 'var(--q-faint, #716C65)' }}>{blockedReason}</Typography>
        )}
        {testState && testState.status !== 'sending' && (
          <Alert severity={testState.status === 'error' ? 'error' : 'success'} sx={{ py: 0 }}>
            {testState.message}
          </Alert>
        )}
      </Box>
    </SectionCard>
  );
}

/**
 * Preview dialog: rendered HTML in a fully sandboxed iframe, plus the plain text version.
 * @param {object} props - Props.
 * @param {{campaign: object, status: string, data: object|null, error: string|null}|null} props.state - Preview state.
 * @param {() => void} props.onClose - Close handler.
 * @returns {JSX.Element} Dialog.
 */
function PreviewDialog({ state, onClose }) {
  const [view, setView] = useState('html');
  const open = Boolean(state);
  const name = state?.campaign?.name || '';

  useEffect(() => {
    if (open) setView('html');
  }, [open, state?.campaign?.key]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="campaign-preview-title">
      <DialogTitle id="campaign-preview-title">{`Preview of ${name}`}</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
        {state?.status === 'loading' && <ListSkeleton rows={4} label="Loading preview" />}
        {state?.status === 'error' && <Alert severity="error">{state.error}</Alert>}
        {state?.status === 'ready' && state.data && (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', columnGap: 1.5, rowGap: 0.5, fontSize: 14 }}>
              <Box component="span" sx={{ color: 'var(--q-muted, #5E5A54)', fontWeight: 600 }}>Subject</Box>
              <Box component="span" data-testid="preview-subject" sx={{ overflowWrap: 'anywhere' }}>{state.data.subject}</Box>
              <Box component="span" sx={{ color: 'var(--q-muted, #5E5A54)', fontWeight: 600 }}>Preheader</Box>
              <Box component="span" data-testid="preview-preheader" sx={{ overflowWrap: 'anywhere' }}>{state.data.preheader}</Box>
            </Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={view}
                onChange={(_, value) => setView(value)}
                aria-label="Preview format"
                sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 600, fontSize: 13.5 } }}
              >
                <Tab value="html" label="Email" id="campaign-preview-tab-html" aria-controls="campaign-preview-panel-html" />
                <Tab value="text" label="Plain text" id="campaign-preview-tab-text" aria-controls="campaign-preview-panel-text" />
              </Tabs>
            </Box>
            {view === 'html' ? (
              <Box role="tabpanel" id="campaign-preview-panel-html" aria-labelledby="campaign-preview-tab-html" sx={{ minWidth: 0 }}>
                <Box
                  component="iframe"
                  title={`Email preview of ${name}`}
                  sandbox=""
                  referrerPolicy="no-referrer"
                  srcDoc={state.data.html || ''}
                  sx={{
                    display: 'block',
                    width: '100%',
                    height: { xs: 480, sm: 620 },
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 'var(--q-radius-lg, 10px)',
                    // Emails are designed on a light background in every theme.
                    bgcolor: '#FFFFFF',
                  }}
                />
              </Box>
            ) : (
              <Box role="tabpanel" id="campaign-preview-panel-text" aria-labelledby="campaign-preview-tab-text" sx={{ minWidth: 0 }}>
                <Box
                  component="pre"
                  data-testid="preview-text"
                  sx={{
                    m: 0,
                    p: 2,
                    maxHeight: 620,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                    fontFamily: 'var(--q-font-mono, ui-monospace, monospace)',
                    fontSize: 13,
                    bgcolor: 'var(--q-bg-subtle, #FAF9F7)',
                    color: 'var(--q-text-2, #3D3A35)',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 'var(--q-radius-lg, 10px)',
                  }}
                >
                  {state.data.text}
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Confirm dialog for a live send. The final Send needs the exact number typed or the box ticked.
 * @param {object} props - Props.
 * @param {{campaign: object, busy: boolean, error: string|null}|null} props.state - Confirm state.
 * @param {number} props.remainingToday - Sends left under today's limit.
 * @param {number} props.dailyCap - Daily limit.
 * @param {() => void} props.onClose - Cancel.
 * @param {(count: number) => void} props.onConfirm - Send with this confirmCount.
 * @returns {JSX.Element} Dialog.
 */
function SendConfirmDialog({ state, remainingToday, dailyCap, onClose, onConfirm }) {
  const [typed, setTyped] = useState('');
  const [ticked, setTicked] = useState(false);
  const open = Boolean(state);
  const count = Number(state?.campaign?.eligibleCount) || 0;
  const busy = Boolean(state?.busy);
  const name = state?.campaign?.name || '';

  useEffect(() => {
    if (open) {
      setTyped('');
      setTicked(false);
    }
  }, [open, state?.campaign?.key, count]);

  const confirmed = ticked || typed.trim().replace(/,/g, '') === String(count);
  const capped = count > remainingToday;
  // What actually goes out now; the server confirms against the full eligible count.
  const take = Math.max(0, Math.min(count, remainingToday));
  const close = () => {
    if (!busy) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="xs"
      fullWidth
      aria-labelledby="campaign-send-title campaign-send-name"
      aria-describedby="campaign-send-desc"
    >
      <DialogTitle id="campaign-send-title">Send this campaign?</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography id="campaign-send-name" component="div" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
          {name}
        </Typography>
        <Box id="campaign-send-desc">
          <Typography variant="body2">
            {capped
              ? `This emails ${num(take)} of ${num(count)} eligible people now. It cannot be undone.`
              : `This emails ${people(count)} right now. It cannot be undone.`}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: capped ? 'var(--q-warning-text, #7F4A00)' : 'var(--q-muted, #5E5A54)' }}>
            {capped
              ? `Sends up to ${num(take)} today because of the daily limit; the other ${num(count - take)} can be sent tomorrow.`
              : `All ${num(count)} fit within today's limit (${num(remainingToday)} of ${num(dailyCap)} left).`}
          </Typography>
        </Box>
        <TextField
          size="small"
          label={capped ? `Type ${count} (the eligible count) to confirm` : `Type ${count} to confirm`}
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          disabled={busy}
          inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
          autoFocus
        />
        <FormControlLabel
          control={<Checkbox checked={ticked} onChange={(event) => setTicked(event.target.checked)} disabled={busy} />}
          label={capped ? `I confirm sending to ${num(take)} of ${num(count)} eligible people` : `I confirm sending to ${people(count)}`}
        />
        {state?.error && <Alert severity="error">{state.error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={() => onConfirm(count)} disabled={!confirmed || busy || count === 0}>
          {busy ? 'Sending' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * History: recent runs and recipients with status chips.
 * @param {object} props - Props.
 * @param {{status: string, data: {runs: object[], sends: object[]}|null, error: string|null}} props.state - History state.
 * @param {Record<string, string>} props.names - Campaign key to name.
 * @param {() => void} props.onRefresh - Reload.
 * @returns {JSX.Element} Section.
 */
function HistorySection({ state, names, onRefresh }) {
  const nameOf = (key) => names[key] || key || 'Unknown';
  const loading = state.status === 'loading';
  return (
    <SectionCard
      title="History"
      description="Recent runs and recipients, newest first."
      aria-label="Campaign history"
      actions={
        <Button
          size="small"
          variant="outlined"
          startIcon={<Refresh fontSize="small" />}
          onClick={onRefresh}
          disabled={loading}
          sx={{ textTransform: 'none', borderRadius: 'var(--q-radius-pill, 999px)' }}
        >
          Refresh history
        </Button>
      }
    >
      {state.status === 'error' && !state.data && <Alert severity="error">{state.error}</Alert>}
      {!state.data && state.status !== 'error' && <ListSkeleton rows={3} label="Loading history" />}
      {state.data && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          {state.status === 'error' && <Alert severity="warning">{`Could not refresh: ${state.error}`}</Alert>}
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>Runs</Typography>
            {state.data.runs.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'var(--q-muted, #5E5A54)' }}>No campaigns sent yet.</Typography>
            ) : (
              <ScrollTable label="Recent runs" maxHeight={320} minWidth={560}>
                <TableHead>
                  <TableRow>
                    <TableCell>Campaign</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Requested</TableCell>
                    <TableCell align="right">Sent</TableCell>
                    <TableCell align="right">Failed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {state.data.runs.map((run) => (
                    <TableRow key={run.id}>
                      <TableCell>{nameOf(run.campaignKey)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(run.startedAt)}</TableCell>
                      <TableCell>
                        {run.test ? <ToneChip tone="info">Test</ToneChip> : <ToneChip tone="accent">Live</ToneChip>}
                        {!run.finishedAt && <ToneChip tone="warning" sx={{ ml: 0.5 }}>Running</ToneChip>}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{run.requested == null ? '' : num(run.requested)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{num(run.sent)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{num(run.failed)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ScrollTable>
            )}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>Recipients</Typography>
            {state.data.sends.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'var(--q-muted, #5E5A54)' }}>No recipients yet.</Typography>
            ) : (
              <ScrollTable label="Recent recipients" maxHeight={420} minWidth={640}>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Campaign</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Sent</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {state.data.sends.map((send) => {
                    const meta = SEND_STATUS_META[send.status] || { label: send.status, tone: 'neutral' };
                    return (
                      <TableRow key={send.id}>
                        <TableCell sx={{ overflowWrap: 'anywhere' }}>{send.email}</TableCell>
                        <TableCell>{nameOf(send.campaignKey)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <ToneChip tone={meta.tone}>{meta.label}</ToneChip>
                          {send.test && <ToneChip tone="info" sx={{ ml: 0.5 }}>Test</ToneChip>}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(send.sentAt, 'Not sent')}</TableCell>
                        <TableCell sx={{ color: 'var(--q-danger-text, #9C2C14)', overflowWrap: 'anywhere' }}>{send.error || ''}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </ScrollTable>
            )}
          </Box>
        </Box>
      )}
    </SectionCard>
  );
}

/** Readable suppression reasons for the Suppress section. */
const SUPPRESSION_REASON_LABELS = {
  unsubscribe: 'unsubscribed',
  manual: 'suppressed by hand',
  bounce: 'bounced',
  complaint: 'marked as spam',
};

/**
 * Suppress an address by hand. Emails carry a mailto unsubscribe option that lands in the
 * info@ inbox; this is how the owner honors those requests without SQL.
 * @returns {JSX.Element} Section.
 */
function SuppressSection() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState({ status: 'idle', message: '' });
  const busy = state.status === 'saving';
  const liveRef = useRef(true);

  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);

  const submit = (event) => {
    event.preventDefault();
    // Re-entry guard instead of disabling the button, so keyboard focus stays put.
    if (busy) return;
    const value = email.trim();
    if (!value) {
      setState({ status: 'error', message: 'Enter an email address.' });
      return;
    }
    setState({ status: 'saving', message: '' });
    suppressAddress(value).then(
      (reply) => {
        if (!liveRef.current) return;
        const shown = reply?.email || value;
        const message = reply?.changed
          ? `${shown} is suppressed. It will not get campaign emails.`
          : `${shown} was already ${SUPPRESSION_REASON_LABELS[reply?.reason] || 'suppressed'}. Nothing changed.`;
        setState({ status: 'done', message });
        if (reply?.changed) setEmail('');
      },
      (error) => {
        if (liveRef.current) setState({ status: 'error', message: errorMessage(error, 'Could not suppress that address.') });
      }
    );
  };

  return (
    <SectionCard
      title="Suppress an address"
      description="For unsubscribe requests that arrive by email at info@quillai.app. Suppressed addresses never get campaign emails."
      aria-label="Suppress an address"
    >
      <Box component="form" onSubmit={submit} noValidate sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          size="small"
          type="email"
          label="Email address"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          inputProps={{ autoComplete: 'off', spellCheck: false }}
          sx={{ flex: '1 1 240px', minWidth: 0 }}
        />
        <Button
          type="submit"
          variant="outlined"
          aria-disabled={busy ? 'true' : undefined}
          sx={{ textTransform: 'none', borderRadius: 'var(--q-radius-pill, 999px)' }}
        >
          {busy ? 'Suppressing' : 'Suppress'}
        </Button>
      </Box>
      <Box aria-live="polite" sx={{ mt: state.message ? 1.5 : 0 }}>
        {state.status === 'error' && <Alert severity="error">{state.message}</Alert>}
        {state.status === 'done' && (
          <Typography variant="body2" sx={{ color: 'var(--q-text-2, #3D3A35)' }}>{state.message}</Typography>
        )}
      </Box>
    </SectionCard>
  );
}

/**
 * Plain-language summary of a live send's outcome.
 * @param {{failed: number, unknown: number, skipped: number, remainingEligible: number, cappedByDailyLimit: boolean}} result - Result.
 * @returns {string} Sentence(s).
 */
export function sendResultDetail(result) {
  const parts = [result.failed ? `${num(result.failed)} failed.` : 'None failed.'];
  if (result.unknown) {
    parts.push(`${num(result.unknown)} not confirmed by Resend, kept as queued so nobody gets it twice.`);
  }
  if (result.skipped) {
    parts.push(`${people(result.skipped)} unsubscribed in the meantime and ${result.skipped === 1 ? 'was' : 'were'} skipped.`);
  }
  parts.push(
    result.remainingEligible
      ? `${people(result.remainingEligible)} still eligible${result.cappedByDailyLimit ? ', held back by the daily limit. Send the rest tomorrow.' : '.'}`
      : 'Nobody left to send to.'
  );
  return parts.join(' ');
}

/**
 * Campaigns tab: manual product email blasts with preview, test send, a confirmed live send and
 * history. Everything is computed and enforced server side by marketing-campaigns; this panel
 * only shows it and passes the exact count the owner confirmed.
 * @returns {JSX.Element} Panel.
 */
export default function ConsoleCampaigns() {
  const [list, setList] = useState({ status: 'loading', data: null, error: null });
  const [history, setHistory] = useState({ status: 'loading', data: null, error: null });
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [tests, setTests] = useState({});
  const [result, setResult] = useState(null);
  const liveRef = useRef(true);

  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);

  const loadList = useCallback(() => {
    setList((prev) => ({ ...prev, status: prev.data ? 'refreshing' : 'loading', error: null }));
    return listCampaigns().then(
      (data) => {
        if (liveRef.current) setList({ status: 'ready', data, error: null });
        return data;
      },
      (error) => {
        if (liveRef.current) setList((prev) => ({ ...prev, status: 'error', error: errorMessage(error, 'Could not load campaigns.') }));
        return null;
      }
    );
  }, []);

  const loadHistory = useCallback(() => {
    setHistory((prev) => ({ ...prev, status: prev.data ? 'refreshing' : 'loading', error: null }));
    campaignHistory({ limit: 50 }).then(
      (reply) => {
        if (liveRef.current) setHistory({ status: 'ready', data: normalizeHistory(reply), error: null });
      },
      (error) => {
        if (liveRef.current) setHistory((prev) => ({ ...prev, status: 'error', error: errorMessage(error, 'Could not load history.') }));
      }
    );
  }, []);

  useEffect(() => {
    loadList();
    loadHistory();
  }, [loadList, loadHistory]);

  const openPreview = useCallback((campaign) => {
    setPreview({ campaign, status: 'loading', data: null, error: null });
    previewCampaign(campaign.key).then(
      (data) => {
        if (liveRef.current) setPreview((prev) => (prev?.campaign.key === campaign.key ? { ...prev, status: 'ready', data } : prev));
      },
      (error) => {
        if (liveRef.current) {
          setPreview((prev) => (prev?.campaign.key === campaign.key ? { ...prev, status: 'error', error: errorMessage(error, 'Could not render the preview.') } : prev));
        }
      }
    );
  }, []);

  const sendTest = useCallback((campaign) => {
    setTests((prev) => ({ ...prev, [campaign.key]: { status: 'sending', message: '' } }));
    testSendCampaign(campaign.key).then(
      (reply) => {
        if (!liveRef.current) return;
        // The function answers 200 even when Resend rejected the email ({ sent: 0, failed: 1, error }).
        const failed = Number(reply?.failed) > 0 || reply?.sent === 0;
        if (failed) {
          const reason = typeof reply?.error === 'string' && reply.error ? reply.error : 'Resend did not accept the email.';
          setTests((prev) => ({ ...prev, [campaign.key]: { status: 'error', message: `The test was not sent: ${reason}` } }));
        } else {
          const to = reply?.email || reply?.to;
          setTests((prev) => ({ ...prev, [campaign.key]: { status: 'sent', message: to ? `Test sent to ${to}.` : 'Test sent to your email.' } }));
        }
        loadHistory();
      },
      (error) => {
        if (liveRef.current) setTests((prev) => ({ ...prev, [campaign.key]: { status: 'error', message: errorMessage(error, 'Could not send the test.') } }));
      }
    );
  }, [loadHistory]);

  const runSend = useCallback((count) => {
    const campaign = confirm?.campaign;
    if (!campaign) return;
    setConfirm((prev) => (prev ? { ...prev, busy: true, error: null } : prev));
    sendCampaign(campaign.key, count).then(
      (reply) => {
        if (!liveRef.current) return;
        setConfirm(null);
        setResult({
          campaignKey: campaign.key,
          name: campaign.name,
          sent: Number(reply?.sent) || 0,
          failed: Number(reply?.failed) || 0,
          unknown: Number(reply?.unknown) || 0,
          skipped: Number(reply?.skipped) || 0,
          remainingEligible: Number(reply?.remainingEligible) || 0,
          cappedByDailyLimit: Boolean(reply?.cappedByDailyLimit),
        });
        loadList();
        loadHistory();
      },
      (error) => {
        if (!liveRef.current) return;
        if (error?.status === 409) {
          // Audience changed (or a run is in flight): refresh the count so the owner confirms again.
          loadList().then((data) => {
            if (!liveRef.current) return;
            const fresh = (data?.campaigns || []).find((c) => c.key === campaign.key);
            setConfirm((prev) => (prev ? { campaign: fresh || prev.campaign, busy: false, error: error.message || 'The audience changed. Check the number and confirm again.' } : prev));
          });
          return;
        }
        setConfirm((prev) => (prev ? { ...prev, busy: false, error: errorMessage(error, 'Could not send.') } : prev));
      }
    );
  }, [confirm, loadList, loadHistory]);

  if (!list.data && list.status === 'error') {
    return (
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={loadList}>Retry</Button>}>
        {list.error}
      </Alert>
    );
  }
  if (!list.data) return <ListSkeleton rows={4} label="Loading campaigns" />;

  const campaigns = Array.isArray(list.data.campaigns) ? list.data.campaigns : [];
  const config = list.data.config || {};
  const missing = missingSecrets(config);
  const gated = gatedEndpoints(config);
  const dailyCap = Number(config.dailyCap) || 0;
  const sentToday = Number(config.sentToday) || 0;
  const remainingToday = Math.max(0, Number(config.remainingToday) || 0);
  const canTest = config.apiKeyConfigured !== false && config.fromConfigured !== false;
  const setupDone = missing.length === 0 && gated.length === 0;
  const canSend = setupDone && remainingToday > 0;
  let blockedReason = null;
  if (!setupDone) blockedReason = 'Finish email setup to send.';
  else if (remainingToday === 0) blockedReason = "Today's sending limit is used up. Send the rest tomorrow.";
  const names = Object.fromEntries(campaigns.map((c) => [c.key, c.name]));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <SetupBanner missing={missing} gated={gated} />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ flex: '1 1 260px', color: 'var(--q-text-2, #3D3A35)' }} data-testid="campaign-order-hint">
          {ORDER_HINT}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Refresh fontSize="small" />}
          onClick={loadList}
          disabled={list.status === 'refreshing' || list.status === 'loading'}
          sx={{ textTransform: 'none', borderRadius: 'var(--q-radius-pill, 999px)' }}
        >
          {list.status === 'refreshing' ? 'Refreshing' : 'Refresh counts'}
        </Button>
      </Box>

      {list.status === 'error' && <Alert severity="warning">{`Could not refresh: ${list.error}`}</Alert>}

      {result && (
        <Alert
          severity={result.failed || result.unknown ? 'warning' : 'success'}
          onClose={() => setResult(null)}
          aria-labelledby="campaign-send-result-title"
          data-testid="campaign-send-result"
        >
          <Typography id="campaign-send-result-title" component="div" sx={{ fontWeight: 600, fontSize: 14 }}>
            {`${result.name}: sent to ${people(result.sent)}`}
          </Typography>
          <Typography component="div" variant="body2">{sendResultDetail(result)}</Typography>
        </Alert>
      )}

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))' } }}>
        <StatTile label="Daily limit" value={num(dailyCap)} />
        <StatTile label="Sent today" value={num(sentToday)} hint="UTC day" />
        <StatTile label="Left today" value={num(remainingToday)} />
      </Box>

      {campaigns.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'var(--q-muted, #5E5A54)' }}>No campaigns are configured.</Typography>
      ) : (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(3, minmax(0, 1fr))' } }}>
          {campaigns.map((campaign) => (
            <CampaignCard
              key={campaign.key}
              campaign={campaign}
              canSend={canSend}
              remainingToday={remainingToday}
              canTest={canTest}
              blockedReason={blockedReason}
              testState={tests[campaign.key]}
              onPreview={openPreview}
              onTest={sendTest}
              onSend={(c) => setConfirm({ campaign: c, busy: false, error: null })}
            />
          ))}
        </Box>
      )}

      <HistorySection state={history} names={names} onRefresh={loadHistory} />

      <SuppressSection />

      <PreviewDialog state={preview} onClose={() => setPreview(null)} />
      <SendConfirmDialog
        state={confirm}
        remainingToday={remainingToday}
        dailyCap={dailyCap}
        onClose={() => setConfirm(null)}
        onConfirm={runSend}
      />
    </Box>
  );
}
